'use client';

import React, { useEffect, useRef, useState } from 'react';
import OptimizedImage from '@/ui/components/shared/OptimizedImage';
import { twJoin } from 'tailwind-merge';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import ButtonLink from '@/ui/components/shared/ButtonLink';
import { ContentBentoBoxTile } from './types/ContentBentoBoxTile';
import './styles.css';
import { extractComponentFromChunk } from '@/ui/utils/tiles';

export default function ContentBentoBox({
  tile,
  trackingPrefix,
}: Readonly<{
  tile: ContentBentoBoxTile;
  trackingPrefix?: string;
}>) {
  const [isFlipped, setIsFlipped] = useState(false);
  const [boxHeight, setBoxHeight] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const imagePosition = tile['image-position']?.options?.[0]?.value;
  const addGradient = tile['add-gradient']?.value;
  const flipSide = tile['flip-side']?.chunks?.[0];
  const flipSideTitle = extractComponentFromChunk(flipSide, 'title', 'RichTextContent');
  const flipSideSubtitle = extractComponentFromChunk(flipSide, 'subtitle', 'RichTextContent');
  const flipSideTextPosition = extractComponentFromChunk(
    flipSide,
    'text-position',
    'SelectionContent',
  );
  const minHeight = tile['min-height']?.options?.[0]?.value;

  // Get the height of the box
  useEffect(() => {
    if (boxRef.current && flipSide) {
      setBoxHeight(boxRef.current.offsetHeight);
    }
  }, [boxRef.current]);

  let minHeightClasses;
  switch (minHeight) {
    case 'md':
      minHeightClasses = 'min-h-[200px] sm:min-h-[300px]';
      break;
    case 'lg':
      minHeightClasses = 'min-h-[300px] sm:min-h-[450px]';
      break;
    case 'xl':
      minHeightClasses = 'min-h-[400px] sm:min-h-[600px]';
      break;
    default:
      minHeightClasses = '';
      break;
  }

  let flipSidePositionClasses;
  switch (flipSideTextPosition?.options?.[0]?.value) {
    case 'center-center':
      flipSidePositionClasses = 'justify-center items-center text-center';
      break;
    case 'center-top':
      flipSidePositionClasses = 'justify-center items-start text-center';
      break;
    case 'center-bottom':
      flipSidePositionClasses = 'justify-center items-end text-center';
      break;
    case 'left-top':
      flipSidePositionClasses = 'justify-start items-start';
      break;
    case 'left-center':
      flipSidePositionClasses = 'justify-start items-center';
      break;
    case 'left-bottom':
      flipSidePositionClasses = 'justify-start items-end';
      break;
    case 'right-top':
      flipSidePositionClasses = 'justify-end items-start text-right';
      break;
    case 'right-center':
      flipSidePositionClasses = 'justify-end items-center text-right';
      break;
    case 'right-bottom':
      flipSidePositionClasses = 'justify-end items-end text-right';
      break;
    default:
      flipSidePositionClasses = 'justify-center items-center text-center';
      break;
  }

  let bgColorClass;
  switch (tile['bg-color']?.options?.[0]?.value) {
    case 'primary-100':
      bgColorClass = 'bg-primary-100';
      break;
    case 'primary-10':
      bgColorClass = 'bg-primary-10';
      break;
    case 'neutral-5':
      bgColorClass = 'bg-neutral-5';
      break;
    default:
      bgColorClass = '';
      break;
  }

  let contentPositionClasses;
  let buttonClasses;
  let gradient;
  switch (tile['text-position']?.options?.[0]?.value) {
    case 'center-center': {
      contentPositionClasses = 'justify-center items-center text-center';
      buttonClasses = 'mx-auto';
      gradient = 'bg-gradient-middle';
      break;
    }
    case 'center-top': {
      contentPositionClasses = 'justify-center items-start text-center';
      buttonClasses = 'mx-auto';
      gradient = 'bg-gradient-top';
      break;
    }
    case 'center-bottom': {
      contentPositionClasses = 'justify-center items-end text-center';
      buttonClasses = 'mx-auto';
      gradient = 'bg-gradient-bottom';
      break;
    }
    case 'left-top': {
      contentPositionClasses = 'justify-start items-start';
      gradient = 'bg-gradient-top';
      break;
    }
    case 'left-center': {
      contentPositionClasses = 'justify-start items-center';
      gradient = 'bg-gradient-middle';
      break;
    }
    case 'left-bottom': {
      contentPositionClasses = 'justify-start items-end';
      gradient = 'bg-gradient-bottom';
      break;
    }
    case 'right-top': {
      contentPositionClasses = 'justify-end items-start text-right';
      buttonClasses = 'ml-auto';
      gradient = 'bg-gradient-top';
      break;
    }
    case 'right-center': {
      contentPositionClasses = 'justify-end items-center text-right';
      buttonClasses = 'ml-auto';
      gradient = 'bg-gradient-middle';
      break;
    }
    case 'right-bottom': {
      contentPositionClasses = 'justify-end items-end text-right';
      buttonClasses = 'ml-auto';
      gradient = 'bg-gradient-bottom';
      break;
    }
    default: {
      contentPositionClasses = 'justify-center items-center';
      buttonClasses = 'mx-auto';
      gradient = 'bg-gradient-middle';
      break;
    }
  }

  let textColorClass;
  switch (tile['text-color']?.options?.[0]?.value) {
    case 'black':
      textColorClass = 'text-black';
      break;
    case 'white':
      textColorClass = 'text-white';
      break;
    default:
      textColorClass = 'text-black';
      break;
  }

  let imageSizeClass;
  switch (tile['image-size']?.options?.[0]?.value) {
    case 'md':
      imageSizeClass = 'max-h-[150px]';
      break;
    case 'lg':
      imageSizeClass = 'max-h-[250px]';
      break;
    case 'xl':
      imageSizeClass = 'max-h-[350px]';
      break;
    default:
      imageSizeClass = 'max-h-[150px]';
      break;
  }

  const handleFlipToggle = () => {
    if (flipSide) {
      setIsFlipped((prev) => !prev);
    }
  };

  const handleFlipKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (flipSide && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      setIsFlipped((prev) => !prev);
    }
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={boxRef}
      className={twJoin(
        'relative',
        flipSide && 'flip-card',
        bgColorClass,
        isFlipped && 'flipped',
        minHeightClasses,
      )}
      onMouseEnter={() => {
        if (flipSide) {
          setIsFlipped(true);
        }
      }}
      onMouseLeave={() => {
        if (flipSide) {
          setIsFlipped(false);
        }
      }}
      onClick={handleFlipToggle}
      onKeyDown={handleFlipKeyDown}
      style={{
        height: flipSide && boxHeight > 0 ? `${boxHeight}px` : '100%',
      }}
    >
      <div className={twJoin('h-full', flipSide && 'flip-card-inner')}>
        <div
          className={twJoin(
            'flex h-full p-5',
            contentPositionClasses,
            flipSide && boxHeight > 0 && 'flip-card-front',
          )}
        >
          <OptimizedImage
            imageComponent={tile['bg-image']}
            sizes="(max-width: 768px) 100vw, 50vw"
            className="z-0 h-full w-full object-cover"
            fill
          />
          {addGradient && <div className={twJoin('absolute inset-0 z-10', gradient)} />}
          <div className={twJoin('z-20 space-y-3 md:space-y-5', textColorClass)}>
            {imagePosition === 'top' && (
              <OptimizedImage
                imageComponent={tile.image}
                sizes="400px"
                className={twJoin('h-full w-auto object-contain', buttonClasses, imageSizeClass)}
                width={300}
                height={200}
              />
            )}
            <RichTextRenderer blocks={tile.title} className="text-2xl md:text-3xl xl:text-4xl" />
            {imagePosition === 'center' && (
              <OptimizedImage
                imageComponent={tile.image}
                sizes="400px"
                className={twJoin('h-full w-auto object-contain', buttonClasses, imageSizeClass)}
                width={300}
                height={200}
              />
            )}
            <RichTextRenderer blocks={tile.subtitle} className="text-md md:text-lg xl:text-xl" />
            <ButtonLink
              ctaComponent={tile.cta}
              className={twJoin('mt-2 w-fit', buttonClasses)}
              trackingPrefix={trackingPrefix}
            />
            {imagePosition === 'bottom' && (
              <OptimizedImage
                imageComponent={tile.image}
                sizes="400px"
                className={twJoin('h-full w-auto object-contain', buttonClasses, imageSizeClass)}
                width={300}
                height={200}
              />
            )}
            <div className="flex flex-wrap items-center justify-center gap-2">
              {tile.logobar?.images?.map((image, index) => (
                <OptimizedImage
                  key={`logobar-box-image-${index}`}
                  image={image}
                  sizes="400px"
                  className="h-8 w-auto sm:h-10"
                  width={200}
                  height={50}
                />
              ))}
            </div>
          </div>
        </div>
        {flipSide && (
          <div
            className={twJoin(
              'flip-card-back flex h-full bg-neutral-100 p-5 text-white',
              flipSidePositionClasses,
            )}
          >
            <div className="max-w-md space-y-3 md:space-y-5">
              {flipSideTitle && (
                <RichTextRenderer
                  blocks={flipSideTitle}
                  className="text-2xl md:text-3xl xl:text-4xl"
                />
              )}
              {flipSideSubtitle && (
                <RichTextRenderer
                  blocks={flipSideSubtitle}
                  className="text-md md:text-lg xl:text-xl"
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
